import React, { useRef, useState } from 'react';
import { Camera, X, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { staffService } from '../../services/staffService';
import { downscaleImage } from '../../lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 5;

export const OrderPhotoUpload = ({ orderId, kind, photos = [], onPhotosChange, canRemove = true, label, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [failedFile, setFailedFile] = useState(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per order`, { position: 'top-center' });
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please use JPG or PNG images', { position: 'top-center' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB', { position: 'top-center' });
      return;
    }
    setUploading(true);
    setFailedFile(null);
    try {
      const blob = await downscaleImage(file, 2048);
      const sigResp = await fetch(`${API_URL}/api/cloudinary/signature?folder=${kind}&resource_type=image`);
      if (!sigResp.ok) throw new Error('Failed to get upload signature');
      const sig = await sigResp.json();

      const formData = new FormData();
      formData.append('file', blob, file.name || 'photo.jpg');
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResp = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: formData });
      if (!uploadResp.ok) throw new Error('Upload failed');
      const result = await uploadResp.json();

      const saved = await staffService.addOrderPhoto(orderId, kind, {
        url: result.secure_url,
        public_id: result.public_id,
      });
      onPhotosChange([...(photos || []), saved.photo]);
      toast.success('Photo added', { position: 'top-center' });
    } catch (err) {
      setFailedFile(file);
      toast.error(err?.response?.data?.detail || 'Photo upload failed — tap retry', { position: 'top-center' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (publicId) => {
    try {
      await staffService.removeOrderPhoto(orderId, kind, publicId);
      onPhotosChange((photos || []).filter((p) => p.public_id !== publicId));
      toast.success('Photo removed', { position: 'top-center' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove photo', { position: 'top-center' });
    }
  };

  return (
    <div data-testid={testId || `${kind}-photos-${orderId}`}>
      {label && <p className="text-xs font-medium text-gray-600 mb-1.5">{label} ({(photos || []).length}/{MAX_PHOTOS})</p>}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {(photos || []).map((photo) => (
          <div key={photo.public_id} className="relative aspect-square rounded-lg overflow-hidden border bg-gray-50 group">
            <img src={photo.url} alt="Order verification" className="w-full h-full object-cover" />
            {canRemove && (
              <button
                type="button"
                onClick={() => handleRemove(photo.public_id)}
                className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 text-white"
                data-testid={`remove-photo-${photo.public_id}`}
                aria-label="Remove photo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {(photos || []).length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square min-h-[64px] rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-400 flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-orange-500 transition-colors"
            data-testid={`add-photo-btn-${orderId}-${kind}`}
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            <span className="text-[10px] font-medium">{uploading ? 'Uploading…' : 'Add photo'}</span>
          </button>
        )}
      </div>
      {failedFile && !uploading && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 h-9 text-orange-600 border-orange-300"
          onClick={() => uploadFile(failedFile)}
          data-testid={`retry-upload-${orderId}-${kind}`}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Retry upload
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          uploadFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
};
